import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { SnackBarService } from '../services/snack-bar.service';
import { AuthService } from '../services/auth.service';
import { IUser } from '../../auth/models/auth.model';

/**
 * OnboardingDataGuard
 *
 * Route guard to ensure onboarding data exists before allowing access to tax-assessment route.
 * Redirects to individual-onboarding if data is missing.
 * Allows back navigation within onboarding flow via query parameter.
 */
export const OnboardingDataGuard: CanActivateFn = (route, state) => {
  const router = inject(Router);
  const authService = inject(AuthService);

  const getUserDetail = (): IUser | null => {
    return authService.getUserProfileData();
  };

  const snackBarService = inject(SnackBarService);

  // Allow navigation if it's a back navigation (has allowBack=true query param)
  if (route.queryParams['allowBack'] === 'true') {
    return true;
  }

  // Allow navigation within onboarding routes by checking the destination route.
  // Use state.url (the full path) rather than route.url, which only contains the
  // segments matched by this route (e.g. "tax-assessment" instead of
  // "/clients/individual-onboarding/tax-assessment"), and would never match below.
  const destinationUrl = state.url;
  const isOnOnboardingRoute =
    destinationUrl.includes('/individual-onboarding/') ||
    destinationUrl.includes('/company-onboarding/') ||
    destinationUrl.includes('/tax-assessment') ||
    destinationUrl.includes('/review-submit');

  if (isOnOnboardingRoute) {
    return true; // Allow navigation within onboarding flow
  }

  // Only enforce onboarding status check for non-onboarding routes
  if (getUserDetail()?.onboardingStatus === 'COMPLETED') {
    return true;
  }

  if (getUserDetail()?.onboardingStatus === 'INCOMPLETED') {
    snackBarService.openSnackBar('Please complete the previous step first.', 'error');
    router.navigateByUrl('/clients/individual-onboarding');
    return false;
  }

  return true;
};
